import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  Plus,
  Mail,
  MessageSquare,
  Edit,
  Trash2,
  Loader2,
  FileText,
  Variable,
  Copy,
  PhoneForwarded,
  Mic,
  Square,
  Play,
  Pause,
  Clock,
  Volume2,
  Sparkles,
  User,
  RefreshCw,
  DollarSign,
  Calendar,
} from 'lucide-react';
import VariablesHelper from '../../components/VariablesHelper';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const TEMPLATE_TYPES = [
  { value: 'email', label: 'Email', icon: Mail, color: 'bg-indigo-100 text-indigo-700' },
  { value: 'sms', label: 'SMS', icon: MessageSquare, color: 'bg-green-100 text-green-700' },
  { value: 'voicemail_drop', label: 'Voicemail Drop', icon: PhoneForwarded, color: 'bg-purple-100 text-purple-700' },
];

const TEAM_TAGS = [
  { value: 'crm', label: 'CRM', color: 'bg-blue-100 text-blue-700' },
  { value: 'franchise', label: 'Franchise', color: 'bg-purple-100 text-purple-700' },
];

export default function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('email');
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    type: 'email',
    subject: '',
    content: '',
    description: '',
    tags: [],
  });
  const [saving, setSaving] = useState(false);

  // Audio recording state for voicemail_drop
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  // TTS (Text-to-Speech) state
  const [voicemailMode, setVoicemailMode] = useState('record'); // 'record' or 'tts'
  const [ttsText, setTtsText] = useState('');
  const [ttsVoices, setTtsVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [selectedGender, setSelectedGender] = useState('');
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [generatingTts, setGeneratingTts] = useState(false);
  const [generatingScript, setGeneratingScript] = useState(false);
  const [ttsAudioUrl, setTtsAudioUrl] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // Fetch current user for AI script generation
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`);
        setCurrentUser(res.data);
      } catch (e) {
        // User not available
        console.error('Failed to fetch user:', e);
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  // Audio recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setRecordedBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(t => t + 1);
      }, 1000);
    } catch (error) {
      toast.error('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // TTS Functions
  const fetchVoices = async (gender = '') => {
    setLoadingVoices(true);
    try {
      const url = gender ? `${API}/elevenlabs/voices?gender=${gender}` : `${API}/elevenlabs/voices`;
      const response = await axios.get(url);
      setTtsVoices(response.data.voices || []);
      if (response.data.voices?.length > 0 && !selectedVoice) {
        setSelectedVoice(response.data.voices[0].voice_id);
      }
    } catch (error) {
      toast.error('Failed to load voices');
      console.error(error);
    } finally {
      setLoadingVoices(false);
    }
  };

  const handleGenderChange = (gender) => {
    setSelectedGender(gender);
    setSelectedVoice(null);
    fetchVoices(gender);
  };

  const generateAIScript = async () => {
    if (!currentUser) {
      toast.error('User information not available');
      return;
    }
    
    setGeneratingScript(true);
    try {
      const response = await axios.post(`${API}/elevenlabs/generate-script`, {
        user_name: currentUser.full_name || currentUser.email?.split('@')[0] || 'Your representative',
        user_phone: currentUser.phone || currentUser.telnyx_phone_number || '(Your phone number)',
        topic: formData.description || null
      });
      setTtsText(response.data.script);
      toast.success('AI script generated! Feel free to edit it.');
    } catch (error) {
      toast.error('Failed to generate script');
      console.error(error);
    } finally {
      setGeneratingScript(false);
    }
  };

  const previewVoice = (previewUrl) => {
    if (previewUrl) {
      const audio = new Audio(previewUrl);
      audio.play();
    }
  };

  const handlePlayPause = (template) => {
    if (playingAudioId === template.id) {
      audioRef.current?.pause();
      setPlayingAudioId(null);
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      const audio = new Audio(`${API}/templates/${template.id}/audio`);
      audio.onended = () => setPlayingAudioId(null);
      audio.play();
      audioRef.current = audio;
      setPlayingAudioId(template.id);
    }
  };

  const handleCreate = async () => {
    // Validation based on type
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    if (formData.type === 'voicemail_drop') {
      if (voicemailMode === 'record' && !recordedBlob) {
        toast.error('Please record a voicemail message');
        return;
      }
      if (voicemailMode === 'tts' && !ttsText.trim()) {
        toast.error('Please enter text for text-to-speech');
        return;
      }
      if (voicemailMode === 'tts' && !selectedVoice) {
        toast.error('Please select a voice');
        return;
      }
    } else {
      if (!formData.content.trim()) {
        toast.error('Content is required');
        return;
      }
      if (formData.type === 'email' && !formData.subject.trim()) {
        toast.error('Subject is required for email templates');
        return;
      }
    }
    
    setSaving(true);
    try {
      // Create the template
      const response = await axios.post(`${API}/templates`, {
        name: formData.name,
        type: formData.type,
        subject: formData.subject || null,
        content: formData.content || null,
        description: formData.description || null,
        tags: formData.tags,
      });
      
      // If voicemail_drop, handle audio based on mode
      if (formData.type === 'voicemail_drop') {
        if (voicemailMode === 'record' && recordedBlob) {
          // Upload recorded audio
          const uploadFormData = new FormData();
          uploadFormData.append('audio', recordedBlob, 'voicemail.webm');
          
          await axios.post(`${API}/templates/${response.data.id}/upload-audio`, uploadFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } else if (voicemailMode === 'tts' && ttsText && selectedVoice) {
          // Generate TTS audio
          setGeneratingTts(true);
          const ttsResponse = await axios.post(`${API}/elevenlabs/tts`, {
            text: ttsText,
            voice_id: selectedVoice,
            template_id: response.data.id
          });
          setGeneratingTts(false);
          
          // Show cost info
          if (ttsResponse.data?.tts_cost) {
            toast.success(`Template created! TTS Cost: ${ttsResponse.data.tts_cost.cost_display}`, {
              duration: 5000
            });
            setCreateDialogOpen(false);
            resetForm();
            fetchTemplates();
            return;
          }
        }
      }
      
      toast.success('Template created');
      setCreateDialogOpen(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create template');
    } finally {
      setSaving(false);
      setGeneratingTts(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    // For non-voicemail types, require content
    if (formData.type !== 'voicemail_drop' && !formData.content?.trim()) {
      toast.error('Content is required');
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API}/templates/${selectedTemplate.id}`, {
        name: formData.name,
        subject: formData.subject,
        content: formData.content,
        description: formData.description,
        tags: formData.tags,
      });
      
      // If voicemail_drop and new recording, upload it
      if (formData.type === 'voicemail_drop' && recordedBlob) {
        const uploadFormData = new FormData();
        uploadFormData.append('audio', recordedBlob, 'voicemail.webm');
        
        await axios.post(`${API}/templates/${selectedTemplate.id}/upload-audio`, uploadFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      toast.success('Template updated');
      setEditDialogOpen(false);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/templates/${selectedTemplate.id}`);
      toast.success('Template deleted');
      setDeleteDialogOpen(false);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: activeTab,
      subject: '',
      content: '',
      description: '',
      tags: [],
    });
    setRecordedBlob(null);
    setRecordingTime(0);
    setIsRecording(false);
    // Reset TTS state
    setVoicemailMode('record');
    setTtsText('');
    setSelectedVoice(null);
    setSelectedGender('');
    setTtsVoices([]);
    setTtsAudioUrl(null);
  };

  const openCreateDialog = () => {
    setFormData({
      name: '',
      type: activeTab,
      subject: '',
      content: '',
      description: '',
      tags: [],
    });
    setRecordedBlob(null);
    setRecordingTime(0);
    setCreateDialogOpen(true);
  };

  // Quick create voicemail with TTS pre-selected
  const openCreateVoicemailTTS = () => {
    setFormData({
      name: '',
      type: 'voicemail_drop',
      subject: '',
      content: '',
      description: '',
      tags: [],
    });
    setRecordedBlob(null);
    setRecordingTime(0);
    setVoicemailMode('tts');
    fetchVoices(); // Pre-load voices
    setCreateDialogOpen(true);
  };

  const openEditDialog = (template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      type: template.type,
      subject: template.subject || '',
      content: template.content || '',
      description: template.description || '',
      tags: template.tags || [],
    });
    setRecordedBlob(null);
    setRecordingTime(0);
    setEditDialogOpen(true);
  };

  const toggleTag = (tagValue) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tagValue)
        ? prev.tags.filter(t => t !== tagValue)
        : [...prev.tags, tagValue]
    }));
  };

  const openDeleteDialog = (template) => {
    setSelectedTemplate(template);
    setDeleteDialogOpen(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const filteredTemplates = templates.filter(t => t.type === activeTab);

  const getTypeInfo = (type) => TEMPLATE_TYPES.find(t => t.value === type) || TEMPLATE_TYPES[0];

  // Helper to format date/time
  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Templates
          </h1>
          <p className="text-slate-600 mt-1">Manage email and SMS templates for lead communications</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={openCreateVoicemailTTS}
            className="border-purple-300 text-purple-700 hover:bg-purple-50"
            data-testid="quick-create-vmd-tts-btn"
          >
            <Volume2 className="h-4 w-4 mr-2" />
            Quick TTS Voicemail
          </Button>
          <Button onClick={openCreateDialog} data-testid="create-template-btn">
            <Plus className="h-4 w-4 mr-2" />
            Create Template
          </Button>
        </div>
      </div>

      {/* Variables Reference Card */}
      <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Variable className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Template Variables</h3>
              <p className="text-sm text-slate-600 mt-1">
                Use these variables in your templates. They will be replaced with actual values when sending.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                {['{FirstName}', '{LastName}', '{CompanyName}', '{Title}', '{Email}', '{Phone}', '{Location}'].map((v) => (
                  <button
                    key={v}
                    onClick={() => copyToClipboard(v)}
                    className="px-2 py-1 bg-white rounded border border-indigo-200 text-xs font-mono text-indigo-600 hover:bg-indigo-50 transition-colors"
                    title="Click to copy"
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Templates List */}
      <Card>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <CardHeader className="pb-0">
            <div className="flex items-center justify-between">
              <TabsList>
                <TabsTrigger value="email" className="gap-2" data-testid="tab-email">
                  <Mail className="h-4 w-4" />
                  Email ({templates.filter(t => t.type === 'email').length})
                </TabsTrigger>
                <TabsTrigger value="sms" className="gap-2" data-testid="tab-sms">
                  <MessageSquare className="h-4 w-4" />
                  SMS ({templates.filter(t => t.type === 'sms').length})
                </TabsTrigger>
                <TabsTrigger value="voicemail_drop" className="gap-2" data-testid="tab-voicemail">
                  <PhoneForwarded className="h-4 w-4" />
                  Voicemail ({templates.filter(t => t.type === 'voicemail_drop').length})
                </TabsTrigger>
              </TabsList>
            </div>
          </CardHeader>
          
          <CardContent className="pt-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-700">No {activeTab === 'voicemail_drop' ? 'voicemail' : activeTab} templates yet</h3>
                <p className="text-slate-500 mt-1">
                  {activeTab === 'voicemail_drop' 
                    ? 'Record pre-recorded voicemails to drop during calls' 
                    : 'Create your first template to get started'}
                </p>
                <Button onClick={openCreateDialog} className="mt-4">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Template
                </Button>
              </div>
            ) : (
              <div className="grid gap-4">
                {filteredTemplates.map((template) => {
                  const typeInfo = getTypeInfo(template.type);
                  const TypeIcon = typeInfo.icon;
                  
                  return (
                    <div
                      key={template.id}
                      className="border border-slate-200 rounded-lg p-4 hover:border-indigo-200 hover:bg-slate-50 transition-colors"
                      data-testid={`template-${template.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${typeInfo.color}`}>
                            <TypeIcon className="h-5 w-5" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-slate-900">{template.name}</h3>
                              {(template.tags || []).map(tag => {
                                const tagInfo = TEAM_TAGS.find(t => t.value === tag);
                                return tagInfo ? (
                                  <Badge key={tag} variant="outline" className={tagInfo.color}>
                                    {tagInfo.label}
                                  </Badge>
                                ) : null;
                              })}
                              {template.type === 'voicemail_drop' && (template.duration_seconds > 0 || (template.is_tts && template.tts_character_count > 0)) && (
                                <Badge variant="outline" className="text-slate-500">
                                  <Clock className="h-3 w-3 mr-1" />
                                  {template.duration_seconds > 0 
                                    ? `${Math.round(template.duration_seconds)}s`
                                    : `~${Math.round(template.tts_character_count / 12.5)}s`
                                  }
                                </Badge>
                              )}
                              {template.is_tts && (
                                <Badge variant="outline" className="text-purple-600 bg-purple-50">
                                  <Volume2 className="h-3 w-3 mr-1" />
                                  TTS
                                </Badge>
                              )}
                              {template.is_tts && template.tts_cost_usd !== undefined && template.tts_cost_usd > 0 && (
                                <Badge variant="outline" className="text-green-600 bg-green-50">
                                  <DollarSign className="h-3 w-3 mr-0.5" />
                                  {template.tts_cost_usd.toFixed(4)}
                                </Badge>
                              )}
                              {template.drop_count > 0 && (
                                <Badge variant="outline" className="text-green-600">
                                  Dropped {template.drop_count}x
                                </Badge>
                              )}
                            </div>
                            {template.subject && (
                              <p className="text-sm text-slate-600 mt-0.5">
                                <span className="text-slate-400">Subject:</span> {template.subject}
                              </p>
                            )}
                            {template.description && (
                              <p className="text-sm text-slate-600 mt-0.5">
                                {template.description}
                              </p>
                            )}
                            {template.content && (
                              <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                                {template.content}
                              </p>
                            )}
                            {/* Play button for voicemail templates */}
                            {template.type === 'voicemail_drop' && template.audio_url && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePlayPause(template)}
                                className="mt-2"
                              >
                                {playingAudioId === template.id ? (
                                  <><Pause className="h-4 w-4 mr-1" /> Pause</>
                                ) : (
                                  <><Play className="h-4 w-4 mr-1" /> Play</>
                                )}
                              </Button>
                            )}
                            <div className="flex items-center gap-3 text-xs text-slate-400 mt-2">
                              <span>Created by {template.created_by_name}</span>
                              {template.created_at && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {formatDateTime(template.created_at)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(template)}
                            data-testid={`edit-template-${template.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => openDeleteDialog(template)}
                            data-testid={`delete-template-${template.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Tabs>
      </Card>

      {/* Create Template Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Template Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Initial Contact"
                  data-testid="template-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={formData.type}
                  onValueChange={(v) => setFormData({ ...formData, type: v })}
                >
                  <SelectTrigger data-testid="template-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TEMPLATE_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        <span className="flex items-center gap-2">
                          <t.icon className="h-4 w-4" />
                          {t.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Team Tags */}
            <div className="space-y-2">
              <Label>Team Visibility</Label>
              <p className="text-xs text-slate-500">Select which teams can use this template. Leave empty for all teams.</p>
              <div className="flex gap-2 mt-1">
                {TEAM_TAGS.map(tag => (
                  <Button
                    key={tag.value}
                    type="button"
                    variant={formData.tags.includes(tag.value) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleTag(tag.value)}
                    className={formData.tags.includes(tag.value) ? tag.color : ''}
                    data-testid={`tag-${tag.value}`}
                  >
                    {tag.label}
                  </Button>
                ))}
              </div>
            </div>
            
            {formData.type === 'email' && (
              <div className="space-y-2">
                <Label>Subject *</Label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="e.g., Following up on our conversation"
                  data-testid="template-subject"
                />
              </div>
            )}
            
            {/* Voicemail Recording Section */}
            {formData.type === 'voicemail_drop' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Description (optional)</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="e.g., Use when calling about demo requests"
                    data-testid="template-description"
                  />
                </div>
                
                {/* Mode Selection Tabs */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="flex border-b">
                    <button
                      type="button"
                      onClick={() => setVoicemailMode('record')}
                      className={`flex-1 py-2 px-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                        voicemailMode === 'record'
                          ? 'bg-purple-100 text-purple-700 border-b-2 border-purple-600'
                          : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <Mic className="h-4 w-4" />
                      Record Voice
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setVoicemailMode('tts');
                        if (ttsVoices.length === 0) {
                          fetchVoices();
                        }
                      }}
                      className={`flex-1 py-2 px-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                        voicemailMode === 'tts'
                          ? 'bg-purple-100 text-purple-700 border-b-2 border-purple-600'
                          : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <Volume2 className="h-4 w-4" />
                      Text-to-Speech
                    </button>
                  </div>
                  
                  <div className="p-4 bg-purple-50">
                    {/* Record Mode */}
                    {voicemailMode === 'record' && (
                      <>
                        <Label className="mb-3 block text-purple-800">Recording *</Label>
                        
                        {!recordedBlob ? (
                          <div className="flex flex-col items-center gap-3">
                            {isRecording ? (
                              <>
                                <div className="flex items-center gap-2">
                                  <div className="h-3 w-3 rounded-full bg-red-500 animate-pulse" />
                                  <span className="text-lg font-mono">{formatTime(recordingTime)}</span>
                                </div>
                                <Button
                                  type="button"
                                  onClick={stopRecording}
                                  variant="destructive"
                                  className="w-full"
                                  data-testid="stop-recording-btn"
                                >
                                  <Square className="h-4 w-4 mr-2" />
                                  Stop Recording
                                </Button>
                              </>
                            ) : (
                              <Button
                                type="button"
                                onClick={startRecording}
                                className="w-full bg-red-600 hover:bg-red-700"
                                data-testid="start-recording-btn"
                              >
                                <Mic className="h-4 w-4 mr-2" />
                                Start Recording
                              </Button>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-purple-700">
                                Recording: {formatTime(recordingTime)}
                              </span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setRecordedBlob(null);
                                  setRecordingTime(0);
                                }}
                              >
                                Re-record
                              </Button>
                            </div>
                            <audio
                              controls
                              src={URL.createObjectURL(recordedBlob)}
                              className="w-full"
                            />
                          </div>
                        )}
                        
                        <p className="text-xs text-purple-600 mt-3">
                          Tip: Keep voicemails under 30 seconds. Include your name and callback number.
                        </p>
                      </>
                    )}
                    
                    {/* TTS Mode */}
                    {voicemailMode === 'tts' && (
                      <div className="space-y-4">
                        {/* AI Script Generation */}
                        <div className="flex items-center justify-between">
                          <Label className="text-purple-800">Script *</Label>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={generateAIScript}
                            disabled={generatingScript}
                            className="text-purple-600 border-purple-300 hover:bg-purple-100"
                          >
                            {generatingScript ? (
                              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            ) : (
                              <Sparkles className="h-4 w-4 mr-1" />
                            )}
                            Create with AI
                          </Button>
                        </div>
                        <Textarea
                          value={ttsText}
                          onChange={(e) => setTtsText(e.target.value)}
                          placeholder="Enter your voicemail script here... Or click 'Create with AI' to generate one automatically."
                          rows={5}
                          className="bg-white"
                          data-testid="tts-script"
                        />
                        <p className="text-xs text-purple-600">
                          AI scripts will mention JobTalk, our AI Agent for recruitment, and include your callback number.
                        </p>
                        
                        {/* Gender Selection */}
                        <div className="space-y-2">
                          <Label className="text-purple-800">Voice Gender</Label>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant={selectedGender === '' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => handleGenderChange('')}
                              className={selectedGender === '' ? 'bg-purple-600' : ''}
                            >
                              All
                            </Button>
                            <Button
                              type="button"
                              variant={selectedGender === 'male' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => handleGenderChange('male')}
                              className={selectedGender === 'male' ? 'bg-purple-600' : ''}
                            >
                              <User className="h-4 w-4 mr-1" />
                              Male
                            </Button>
                            <Button
                              type="button"
                              variant={selectedGender === 'female' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => handleGenderChange('female')}
                              className={selectedGender === 'female' ? 'bg-purple-600' : ''}
                            >
                              <User className="h-4 w-4 mr-1" />
                              Female
                            </Button>
                          </div>
                        </div>
                        
                        {/* Voice Selection */}
                        <div className="space-y-2">
                          <Label className="text-purple-800">Select Voice *</Label>
                          {loadingVoices ? (
                            <div className="flex items-center justify-center py-4">
                              <Loader2 className="h-5 w-5 animate-spin text-purple-600" />
                              <span className="ml-2 text-sm text-purple-600">Loading voices...</span>
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                              {ttsVoices.map((voice) => (
                                <button
                                  key={voice.voice_id}
                                  type="button"
                                  onClick={() => setSelectedVoice(voice.voice_id)}
                                  className={`p-2 rounded-lg border text-left transition-all ${
                                    selectedVoice === voice.voice_id
                                      ? 'border-purple-500 bg-purple-100 ring-2 ring-purple-300'
                                      : 'border-gray-200 bg-white hover:border-purple-300'
                                  }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium text-sm">{voice.name}</span>
                                    {voice.preview_url && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          previewVoice(voice.preview_url);
                                        }}
                                        className="p-1 hover:bg-purple-200 rounded"
                                        title="Preview voice"
                                      >
                                        <Play className="h-3 w-3 text-purple-600" />
                                      </button>
                                    )}
                                  </div>
                                  <div className="flex gap-1 mt-1">
                                    {voice.gender && (
                                      <span className="text-xs px-1.5 py-0.5 bg-gray-100 rounded text-gray-600">
                                        {voice.gender}
                                      </span>
                                    )}
                                    {voice.accent && (
                                      <span className="text-xs px-1.5 py-0.5 bg-blue-50 rounded text-blue-600">
                                        {voice.accent}
                                      </span>
                                    )}
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                          {!loadingVoices && ttsVoices.length === 0 && (
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => fetchVoices(selectedGender)}
                              className="w-full"
                            >
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Load Voices
                            </Button>
                          )}
                        </div>
                        
                        {ttsText && selectedVoice && (
                          <p className="text-xs text-green-600 bg-green-50 p-2 rounded">
                            ✓ Ready to generate! Audio will be created when you save the template.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Content for Email/SMS */}
            {formData.type !== 'voicemail_drop' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Content *</Label>
                  <VariablesHelper
                    compact
                    onInsert={(variable) => {
                      setFormData({ ...formData, content: formData.content + variable });
                    }}
                  />
                </div>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Hi {FirstName},&#10;&#10;Thank you for your interest in..."
                  rows={10}
                  className="font-mono text-sm"
                  data-testid="template-content"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving || generatingTts} data-testid="save-template-btn">
              {(saving || generatingTts) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {generatingTts ? 'Generating Audio...' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Template Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="edit-template-name"
              />
            </div>
            
            {/* Team Tags */}
            <div className="space-y-2">
              <Label>Team Visibility</Label>
              <p className="text-xs text-slate-500">Select which teams can use this template. Leave empty for all teams.</p>
              <div className="flex gap-2 mt-1">
                {TEAM_TAGS.map(tag => (
                  <Button
                    key={tag.value}
                    type="button"
                    variant={formData.tags.includes(tag.value) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleTag(tag.value)}
                    className={formData.tags.includes(tag.value) ? tag.color : ''}
                    data-testid={`edit-tag-${tag.value}`}
                  >
                    {tag.label}
                  </Button>
                ))}
              </div>
            </div>
            
            {formData.type === 'email' && (
              <div className="space-y-2">
                <Label>Subject *</Label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  data-testid="edit-template-subject"
                />
              </div>
            )}
            
            {/* Voicemail Edit Section */}
            {formData.type === 'voicemail_drop' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Description (optional)</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="e.g., Use when calling about demo requests"
                  />
                </div>
                
                <div className="border rounded-lg p-4 bg-purple-50">
                  <Label className="mb-3 block text-purple-800">Recording</Label>
                  
                  {selectedTemplate?.audio_url && !recordedBlob && (
                    <div className="mb-3">
                      <p className="text-sm text-purple-700 mb-2">Current recording:</p>
                      <audio
                        controls
                        src={`${API}/templates/${selectedTemplate.id}/audio`}
                        className="w-full"
                      />
                    </div>
                  )}
                  
                  {!recordedBlob ? (
                    <div className="flex flex-col items-center gap-3">
                      {isRecording ? (
                        <>
                          <div className="flex items-center gap-2">
                            <div className="h-3 w-3 rounded-full bg-red-500 animate-pulse" />
                            <span className="text-lg font-mono">{formatTime(recordingTime)}</span>
                          </div>
                          <Button
                            type="button"
                            onClick={stopRecording}
                            variant="destructive"
                            className="w-full"
                          >
                            <Square className="h-4 w-4 mr-2" />
                            Stop Recording
                          </Button>
                        </>
                      ) : (
                        <Button
                          type="button"
                          onClick={startRecording}
                          className="w-full bg-red-600 hover:bg-red-700"
                        >
                          <Mic className="h-4 w-4 mr-2" />
                          {selectedTemplate?.audio_url ? 'Re-record' : 'Start Recording'}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-purple-700">New recording: {formatTime(recordingTime)}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setRecordedBlob(null);
                            setRecordingTime(0);
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                      <audio
                        controls
                        src={URL.createObjectURL(recordedBlob)}
                        className="w-full"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Content for Email/SMS */}
            {formData.type !== 'voicemail_drop' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Content *</Label>
                  <VariablesHelper
                    compact
                    onInsert={(variable) => {
                      setFormData({ ...formData, content: formData.content + variable });
                    }}
                  />
                </div>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  rows={10}
                  className="font-mono text-sm"
                  data-testid="edit-template-content"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdate} disabled={saving} data-testid="update-template-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Update Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{selectedTemplate?.name}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
